import { ConfigProvider, DatePicker, Pagination } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Dayjs } from 'dayjs';
import '../../../styles.scss';

import { SearchOutlined } from '@ant-design/icons';
import { AgGridReact } from 'ag-grid-react';
import viVN from "antd/locale/vi_VN";
import { useSelector } from 'react-redux';
import { ICustomer, ListField } from '../../../../../api/Customer_V2/customerApi_V2';
import { NodataTable } from '../../../../../components/NoDataTable';
import useLoading from '../../../../../hook/useLoading';
import useToast from '../../../../../hook/useToast';
import { formatNumber } from '../../../../../services/mixin';
import { selectorCategoryCarIds } from '../../../../../store/slices/categoryCarSlice';
import { selectorInfoUser } from '../../../../../store/slices/infoUserSlice';
import { CustomLoadingOverlayTable, dataPagination } from '../../../../../until';
import { IPagination } from '../../../../Customers/CustomerGroup/Component/AddGroup/AddNewGroup';
import { ColumnKey } from '../../../../Customers/Tabs/CarListCardClone';
import { FlattenedField } from '../../../../CustomersV2/indexV2';
import { generateColumnsFromCodesAgGridReactRecordCar } from '../../ServiceRecords/ContactInformation';
import { IDataFilter } from '../TabInteractionHistory';
import dayjs from 'dayjs';
import { NO_VIEW } from '../../../../../services/data';

const { RangePicker } = DatePicker;

type PaginationKey = "info";
interface IDataFilterActive {
  info: FlattenedField[];
}

interface IDataFilterNoActive {
  info: ListField[];
}

interface Props {
  loadingTable: boolean
  dataFieldsActive: FlattenedField[]
  idCustomer?: number
  data: ICustomer[]
  pagination: IPagination
  dataFilter: IDataFilter

  onPaginationChange: (page: number, pageSize: number) => void
  setDataFilter: React.Dispatch<React.SetStateAction<IDataFilter>>
}

const TabMarketing: React.FC<Props> = ({
  loadingTable,
  dataFieldsActive, pagination,
  idCustomer, data, dataFilter,
  onPaginationChange, setDataFilter
}) => {

  const [columnsMapShow, setColumnsMapShow] = useState<ColumnKey[]>([]);
  // Lưu tạm giá trị ngày đang chọn, chưa commit vào dataFilter
  const pendingDatesRef = useRef<[Dayjs | null, Dayjs | null] | null>(null);
  //#region Cấu hình cột
  const memoizedColumnDefs = useMemo(() => {
    return generateColumnsFromCodesAgGridReactRecordCar(
      columnsMapShow,
      dataFieldsActive,
      undefined, // actionColumn
      undefined, // onToggleStatus
      { setDataFilterList: () => { } }, // headerActions
      undefined, // listFilterIcon
      undefined, // dataFilterList
      idCustomer, // customerId
      [""] // listFieldCodeLink
    );
  }, [columnsMapShow, dataFieldsActive]);

  const tableData = useMemo(() => {
    return data.map((item) => {
      const fieldValues = item.fields.reduce((acc: Record<string, any>, field) => {
        acc[field.fieldCode] = field.view === false ? NO_VIEW : field.value;
        return acc;
      }, {});

      return {
        ...item,
        key: item.id.toString(),
        ...fieldValues
      };
    });
  }, [data]);

  useEffect(() => {
    const codes = dataFieldsActive.map((f) => f.fieldCode);
    setColumnsMapShow(codes);
  }, [dataFieldsActive]);
  //#endregion


  return (
    <div className="custom-page-css w-full flex flex-col items-center  gap-3 ">
      <div className="custom-page-css  w-full flex gap-3">
        <div className="custom-page-css custom-input-search w-1-3 flex items-center justify-between border-F2F4F7 bg-FCFCFD rounded-2  pl-3  overflow-hidden">
          <input
            placeholder="Tìm kiếm thông tin khách hàng"
            className=' custom-input-css  custom-page-css  border-none w-full text-truncate'
            value={dataFilter.marketting.search}
            onChange={(e) => setDataFilter({
              ...dataFilter, marketting: {
                ...dataFilter.marketting,
                search: e.target.value
              },
            })}
          />
          <div className="border-l-F2F4F7 h-full d-flex align-center">
            <SearchOutlined className="text-sm text-98A2B3 px-9-px" />
          </div>
        </div>

        <div className="">
          <RangePicker
            popupClassName=" custom-input-date"
            placeholder={["Từ ngày", "Đến ngày"]}
            format="DD/MM/YYYY"
            allowClear
            value={
              dataFilter.marketting.fromDate && dataFilter.marketting.toDate ?
                [
                  dayjs(dataFilter.marketting.fromDate),
                  dayjs(dataFilter.marketting.toDate),
                ] :
                null
            }
            onCalendarChange={(dates) => {
              // Chỉ lưu tạm vào ref, chưa setDataFilter
              pendingDatesRef.current = dates ?? null;
            }}
            onOpenChange={(open) => {
              // Khi dropdown đóng mới commit giá trị vào dataFilter
              if (!open) {
                const dates = pendingDatesRef.current;
                setDataFilter(prev => ({
                  ...prev,
                  marketting: {
                    ...prev.marketting,
                    fromDate: dates?.[0]?.format("YYYY/MM/DD") ?? "",
                    toDate: dates?.[1]?.format("YYYY/MM/DD") ?? "",
                  },
                }));
                pendingDatesRef.current = null;
              }
            }}
          />
        </div>
      </div>
      <div className="ag-theme-alpine custom-table-ag custom-css-table"
        style={{ width: '100%', height: tableData.length === 0 ? 250 : tableData.length < 10 ? tableData.length * 65 + 65 : 650 }}
      >
        <AgGridReact
          className="my-auto-height-grid"
          getRowStyle={(params: any) => ({
            backgroundColor: params?.rowIndex % 2 ? '#f7f9fc' : '#ffffff',
          })}
          rowData={tableData}
          columnDefs={memoizedColumnDefs}
          rowHeight={55}
          rowSelection="single"
          overlayNoRowsTemplate={NodataTable}
          loading={loadingTable}
          loadingOverlayComponent={CustomLoadingOverlayTable}
          onGridReady={(params) => {
            params.api.sizeColumnsToFit();
          }}
        />
      </div>
      <ConfigProvider
        locale={viVN}
      >
        <div className="pagination-block">
          <Pagination
            showSizeChanger
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            itemRender={(page, type, originalElement) => {
              // Chỉ custom khi là nút số trang
              if (type === 'page') {
                return <span>{formatNumber(page)}</span>;
              }
              // prev / next / jump-prev / jump-next giữ nguyên
              return originalElement;
            }}
            showTotal={() =>
              `Tổng cộng ${dataPagination(
                pagination.total,
                pagination.pageSize,
                pagination.current
              ).totalPage} trang`
            }
            className="rs-pagination"
            onChange={onPaginationChange}
          />
        </div>
      </ConfigProvider>

    </div>
  )
}
export default TabMarketing